<template>
    <modal-component
        :visible="visible"
        id="invoice-misc-modal"
        size="lg"
        :title="payload.id ? 'Edit Invoice' : 'Create Invoice'"
        @close="$emit('close')"
    >
        <div>
            <previous-claim
                ref="previousClaims"
                v-if="helpers._get(payload, 'previous_claims', []).length"
                :invoice="payload"
            ></previous-claim>

            <div class="row form-group">
                <div class="col-sm-6">
                    <label class="col-sm-12">Patient:</label>
                    <div class="col-sm-12">
                        <select
                            :disabled="payload.id || newContract || payload.cob_parent_claim_id"
                            class="form-control form-control-sm"
                            v-model="payload.patient_id"
                        >
                            <option selected :value="null">Choose one</option>
                            <option :value="p.id" v-for="p in patients" data-private="lipsum">{{
                                p.name
                            }}</option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-6">
                    <label class="col-sm-12 mt-3 mt-sm-0">Therapist:</label>
                    <div class="col-sm-12">
                        <select
                            class="form-control form-control-sm"
                            v-model="payload.therapist_id"
                            :disabled="newContract || payload.cob_parent_claim_id"
                        >
                            <option selected disabled hidden :value="null">Choose one</option>
                            <option :value="t.id" v-for="t in therapists">{{ t.name }}</option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="row form-group">
                <div class="col-12 col-sm-6">
                    <label class="col-sm-12">Service date:</label>
                    <div class="col-sm-12">
                        <div
                            :class="{ hide: payload.claimed_at }"
                            class="d-flex flex-column justify-content-start align-items-start"
                        >
                            <v-date-picker
                                :available-dates="
                                    newContract || payload.cob_parent_claim_id
                                        ? { start: '9999-12-31', end: null }
                                        : {}
                                "
                                class="form-control form-control-sm"
                                popover-align="right"
                                v-model="payload.serviced_at"
                            ></v-date-picker>
                        </div>
                        <div
                            v-if="payload.claimed_at"
                            class="d-flex flex-column justify-content-start align-items-start"
                        >
                            <input
                                type="text"
                                disabled
                                class="form-control form-control-sm"
                                :value="
                                    payload.serviced_at
                                        ? moment(payload.serviced_at).format('MM/DD/YYYY')
                                        : ''
                                "
                            />
                        </div>
                    </div>
                </div>

                <div class="col-12 col-sm-6">
                    <template v-if="title === 'Private Pay'">
                        <label class="col-sm-12">Charge Description</label>
                        <div class="col-sm-12">
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model="payload.notes"
                            />
                        </div>
                    </template>
                </div>
            </div>

            <template v-if="title !== 'Private Pay'">
                <div class="row form-group">
                    <div class="col-12 col-sm-6">
                        <label class="col-sm-12">Contract:</label>
                        <div class="col-sm-12">
                            <select
                                class="form-control form-control-sm"
                                v-model="payload.contract_id"
                            >
                                <option selected disabled hidden :value="null">Choose one</option>
                                <option
                                    :value="contract.id"
                                    v-for="contract in contracts"
                                    :disabled="
                                        newContract && previousContractIds.includes(contract.id)
                                    "
                                    >{{
                                        `${prettyType(contract.funding_source.type)} - ${
                                            contract.funding_source.name
                                        }`
                                    }}</option
                                >
                            </select>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6" v-if="contract">
                        <label class="col-sm-12 mt-3 mt-sm-0">Identification Number:</label>
                        <div class="col-sm-12">
                            <input
                                type="text"
                                disabled
                                class="form-control form-control-sm"
                                :value="contract.identifier"
                            />
                        </div>
                    </div>
                </div>

                <template
                    v-if="
                        helpers._get(contract, 'funding_source.type', null) === 'insurance_provider'
                    "
                >
                    <div class="form-group row mb-2">
                        <div class="col-12 col-sm-6">
                            <label for="service-location" class="col-sm-12"
                                >Choose Place of Service:</label
                            >
                            <div class="col-sm-12">
                                <select
                                    class="form-control form-control-sm"
                                    id="service-location"
                                    v-model="payload.service_location_id"
                                >
                                    <option value="0"></option>
                                    <option v-for="p in placesOfService" :value="p.id">{{
                                        `[${p.code}] ${p.name}`
                                    }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6">
                            <label for="report-cpt-approve" class="col-sm-12 mt-3 mt-sm-0"
                                >Choose CPT code:</label
                            >
                            <div class="col-sm-12" v-if="contract && contract.cpt_codes">
                                <select
                                    class="form-control form-control-sm"
                                    id="report-cpt-approve"
                                    v-model="payload.preferred_cpt_code_id"
                                >
                                    <option v-for="c in contract.cpt_codes" :value="c.id">{{
                                        `${c.code} - ${c.service_nickname}`
                                    }}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="form-group row mb-2" v-if="Array.isArray(payload.diagnoses)">
                        <div class="col-12 col-sm-6">
                            <label for="diagnoses" class="col-sm-12"
                                >Choose diagnoses :
                                <i
                                    v-if="title === 'Insurance Providers'"
                                    class="fa fa-info-circle text-info"
                                    v-tooltip="
                                        'Make sure at least one diagnosis code is selected before submitting an insurance claim. Use CTRL+click (Windows) or CMD+click (Mac) to toggle a selection'
                                    "
                                ></i
                            ></label>
                            <div class="col-sm-12" v-if="contract && contract.diagnoses">
                                <vw-multi-select
                                    id="diagnoses"
                                    v-model="payload.diagnoses"
                                    :template="'bootstrap'"
                                    :iconset="'font-awesome'"
                                    :styles="{ input: { width: '100%' } }"
                                    :list="contract.diagnoses"
                                    :value-property="'code'"
                                    :searchable="true"
                                    :tags="true"
                                    :selected-list-display-function="item => item.code"
                                    :list-item-display-function="
                                        item => `${item.code} - ${item.service_nickname}`
                                    "
                                ></vw-multi-select>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="!helpers._get(payload, 'diagnoses', []).length"
                        class="small font-italic mb-3 col-12"
                    >
                        <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
                        At least one diagnosis code must be selected in order to submit insurance
                        claims
                    </div>
                </template>

                <template
                    v-if="
                        helpers._get(contract, 'funding_source.type', false) &&
                            contract.funding_source.type !== 'insurance_provider'
                    "
                >
                    <div class="form-group row mb-2">
                        <div class="col-12 col-sm-6">
                            <label for="report-services" class="col-sm-12"
                                >Choose service code:</label
                            >
                            <template v-if="contract && contract.service_codes">
                                <select
                                    class="col-sm-12form-control form-control-sm"
                                    id="report-services"
                                    v-model="payload.service_codes"
                                >
                                    <option v-for="c in contract.service_codes" :value="[c.code]">{{
                                        `${c.code} - ${c.service_nickname}`
                                    }}</option>
                                </select>
                            </template>
                        </div>
                    </div>
                </template>
            </template>

            <div class="row form-group">
                <div class="col-12 col-sm-6">
                    <label class="col-sm-12" for="expected-amount">Expected amount:</label>
                    <div class="col-sm-12">
                        <div class="input-group input-group-sm">
                            <div class="input-group-prepend">
                                <div class="input-group-text">$</div>
                            </div>
                            <input
                                type="text"
                                id="expected-amount"
                                placeholder="0.00"
                                class="form-control form-control-sm text-right"
                                v-model.number="payload.expected_amount"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6">
                    <label class="col-sm-12 mt-3 mt-sm-0" for="service-hours">Service hours:</label>
                    <div class="col-sm-12">
                        <div class="input-group input-group-sm">
                            <input
                                :disabled="newContract || payload.cob_parent_claim_id"
                                type="text"
                                id="service-hours"
                                placeholder="0.00"
                                class="form-control form-control-sm text-right"
                                v-model.number="payload.service_hours"
                            />
                            <div class="input-group-append">
                                <div class="input-group-text">h</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="
                    payload.can_submit_claim &&
                        payload.has_submitted_claim &&
                        claim &&
                        claim.payment_control_number &&
                        !payload.cob_parent_claim_id &&
                        !newContract
                "
                class="alert alert-info alert-dismissible fade show d-flex align-items-center mb-3"
                role="alert"
            >
                <i class="fa fa-info-circle text-secondary mr-2"></i>
                This claim will be submitted as a correction for
                {{ claim.payment_control_number }}.
            </div>

            <div class="col-12">
                <errors :errors="errors"></errors>

                <div class="d-flex w-100 justify-content-end">
                    <button
                        id="save-submit"
                        v-if="
                            (!payload.id ||
                                (payload.can_submit_claim && !payload.has_submitted_claim)) &&
                                helpers._get(contract, 'funding_source.type', null) ===
                                    'insurance_provider'
                        "
                        class="btn btn-sm btn-outline-primary mr-3"
                        @click="saveInvoice(true)"
                    >
                        Save and submit claim
                    </button>
                    <button
                        class="btn btn-sm btn-primary text-white"
                        id="save"
                        @click="newContract ? createSecondaryInvoice() : saveInvoice()"
                    >
                        Save
                    </button>
                    <button
                        id="save-resubmit"
                        v-if="
                            payload.can_submit_claim && payload.has_submitted_claim && !newContract
                        "
                        class="btn btn-sm btn-primary text-white ml-3"
                        @click="saveInvoice(true)"
                    >
                        Save and resubmit claim
                    </button>
                </div>
            </div>
        </div>
    </modal-component>
</template>

<script>
import Dots from "../ui/DotDotDot";
import ModalComponent from "../ui/ModalComponent";
import PreviousClaim from "./FundingSourcesPreviousClaim";
import { errors } from "../../plugins/errors";
import { helpers } from "../../plugins/helpers";
import { fixDatePickers } from "../../plugins/fixDatePickers";

export default {
    name: "InvoiceMiscCharge",
    props: ["organization", "title", "patients", "therapists", "placesOfService", "claim"],
    components: { ModalComponent, Dots, PreviousClaim },
    mixins: [errors, fixDatePickers],
    data() {
        return {
            helpers,
            newContract: false,
            visible: false,
            success: false,
            isStripeMounted: false,
            payload: this.generatePayload(),
        };
    },
    watch: {
        "payload.patient_id"() {
            this.payload.contract_id = null;
        },

        "payload.contract_id"() {
            this.payload.preferred_cpt_code_id = null;
        },

        contract() {
            if (!this.payload.id) {
                //We don't want to populate existing invoices, or the user could be misled to think the diagnoses are already selected and not save
                this.payload.diagnoses = [];
                if (this.contract && this.contract.diagnoses && this.contract.diagnoses.length) {
                    this.contract.diagnoses.forEach(diagnosis => {
                        this.payload.diagnoses.push(diagnosis.code); //TODO: shoud be array?
                    });
                }
            }
        },

        visible() {
            this.removePickerMinWidth();
        },
    },

    computed: {
        patient() {
            if (_.get(this.payload, "patient_id", null)) {
                let patient = this.patients.find(patient => patient.id === this.payload.patient_id);
                if (patient) {
                    return patient;
                }
            }
            return null;
        },
        contracts() {
            if (this.patient) {
                let contractFilter = "";
                switch (this.title) {
                    case "Schools":
                        contractFilter = "school";
                        break;
                    case "Regional Centers":
                        contractFilter = "regional_center";
                        break;
                    case "Easterseals":
                        contractFilter = "easter_seal";
                        break;
                    case "Insurance":
                        contractFilter = "insurance_provider";
                        break;
                    default:
                        contractFilter = "insurance_provider";
                        break;
                }
                const contracts = this.patient.contracts.filter(
                    contract => contract.funding_source.type === contractFilter
                );
                if (contracts) {
                    return contracts;
                }
            }
            return [];
        },

        previousContractIds() {
            const previousClaims = _.get(this.payload, "previous_claims", []);
            return Array.from(
                new Set(previousClaims.map(claim => _.get(claim, "charges.0.contract.id", null)))
            ).filter(id => id); // Takes the unique, non-null values of contract_id present in the claims
        },

        contract() {
            if (_.get(this.payload, "contract_id", null)) {
                return this.contracts.find(contract => contract.id === this.payload.contract_id);
            }
            return {};
        },
    },
    methods: {
        generatePayload() {
            return {
                id: null,
                patient_id: null,
                therapist_id: null,
                contract_id: null,
                serviced_at: null,
                notes: null,
                can_submit_claim: null,
                has_submitted_claim: null,
                diagnoses: [],
                preferred_cpt_code_id: null,
                service_location_id: null,
                previous_claims: [],
            };
        },

        async fetchPreviousClaims(parentClaimId) {
            return await axios.get(
                `/api/organizations/${
                    this.organization.id
                }/secondary_invoices/${parentClaimId}/preview`
            );
        },

        async parseEditPayload(invoice, charge) {
            this.$set(this.payload, "expected_amount", invoice.expected_amount); // Set the expected amount of the edited invoice first, as it may be overridden by fetchPreviousClaims

            if (invoice.cob_parent_claim_id) {
                this.$set(this.payload, "cob_parent_claim_id", invoice.cob_parent_claim_id);
            }

            const claimId = invoice.cob_parent_claim_id || _.get(this.claim, "id", false);
            if (invoice.cob_parent_claim_id || this.newContract) {
                const previousClaimsData = await this.fetchPreviousClaims(claimId); // Fetch previous claims either for the cob_parent_claim or the current claim
                this.$set(this.payload, "previous_claims", previousClaimsData.data.previous_claims);
                if (this.newContract) {
                    this.$set(
                        this.payload,
                        "expected_amount",
                        previousClaimsData.data.expected_amount
                    );
                }
            }

            this.$set(this.payload, "id", invoice.id);
            this.$set(this.payload, "patient_id", invoice.patient.id);
            this.$set(this.payload, "therapist_id", _.get(invoice, "therapists.0.id", null));
            this.$set(
                this.payload,
                "serviced_at",
                new Date(invoice.serviced_at || invoice.billing_start_at)
            );
            this.$set(this.payload, "notes", invoice.notes);
            this.$set(this.payload, "can_submit_claim", invoice.can_submit_claim);
            this.$set(this.payload, "service_hours", invoice.service_hours);

            this.$set(this.payload, "has_submitted_claim", invoice.has_submitted_claim);

            if (!this.newContract) {
                setTimeout(() => {
                    this.$set(this.payload, "contract_id", _.get(charge, "contract.id", null));

                    setTimeout(() => {
                        this.$set(
                            this.payload,
                            "preferred_cpt_code_id",
                            _.get(charge, "preferred_cpt_code_id", null)
                        );
                    }, 0); //moves execution after contract is set and selects are populated

                    this.$set(
                        this.payload,
                        "service_location_id",
                        _.get(charge, "service_location_id", null)
                    );

                    this.$set(
                        this.payload,
                        "diagnoses",
                        _.get(charge, "diagnoses", []).map(code => code.code)
                    );

                    // legacy data will not have a preferred_cpt_code_id
                    // default to one for backwards compatibility
                    if (
                        !charge.preferred_cpt_code_id &&
                        charge.cpt_codes &&
                        charge.cpt_codes.length
                    ) {
                        let code = _.get(charge, "contract.cpt_codes", []).find(
                            code => code.code === charge.cpt_codes[0].code
                        );
                        if (code) {
                            this.payload.preferred_cpt_code_id = code.id;
                        }
                    }
                }, 0);
            } else {
                this.$set(this.payload, "contract_id", null);
                this.$set(this.payload, "preferred_cpt_code_id", null);
                this.$set(this.payload, "service_location_id", null);
                this.$set(this.payload, "diagnoses", []);
            }
        },

        preparePayload(payload) {
            payload.billing_start_at = payload.billing_start_at || payload.serviced_at;
            payload.billing_end_at = payload.billing_end_at || payload.serviced_at;
            payload.serviced_at = payload.serviced_at
                ? this.moment(payload.serviced_at).format("YYYY-MM-DD")
                : null;
            payload.billing_start_at = payload.billing_start_at
                ? this.moment(payload.billing_start_at).format("YYYY-MM-DD")
                : null;
            payload.billing_end_at = payload.billing_end_at
                ? this.moment(payload.billing_end_at).format("YYYY-MM-DD")
                : null;
            if (!payload.contract_id) {
                delete payload.contract_id;
                delete payload.preferred_cpt_code_id;
            }
            if (_.get(this.contract, "funding_source.type", null) !== "insurance_provider") {
                delete payload.preferred_cpt_code_id;
                delete payload.has_submitted_claim;
                delete payload.can_submit_claim;
            }
            return payload;
        },

        saveInvoice(resubmit = false) {
            // Charge value must be the expected amount while only one charge is allowed
            this.setErrors();
            if (!this.validatePayload()) return;
            let payload = this.preparePayload({ ...this.payload });

            axios({
                method: payload.id ? "PATCH" : "POST",
                url: payload.id
                    ? `/api/organizations/${this.organization.id}/invoices/${this.payload.id}`
                    : `/api/organizations/${this.organization.id}/invoices`,
                data: payload,
            })
                .then(response => {
                    this.$emit("fetch-invoices");
                    if (resubmit) {
                        let invoiceId = this.payload.id || response.data.data.id;
                        this.$emit("resubmit-claim", invoiceId);
                    }
                    //if (payload.id) {
                    //    this.$emit("local-update", r.data.data);
                    //} else {
                    this.$emit("fetch-page");
                    //}

                    this.resetModal();
                })
                .catch(e => {
                    console.error(e);
                    const errors = _.get(e, "response.data.errors", {});
                    const message = _.get(e, "response.data.message", {});
                    if (
                        typeof errors === "object" &&
                        Object.keys(errors) &&
                        Object.keys(errors).length
                    ) {
                        this.setErrors({ ...errors });
                    } else if (typeof message === "string") {
                        this.setErrors([{ message }]);
                    } else if (isArray(message)) {
                        this.setErrors([...message]);
                    }
                });
        },

        createSecondaryInvoice() {
            this.setErrors();

            const payload = { ...this.payload };
            const claimId = payload.cob_parent_claim_id || _.get(this.claim, "id", false);
            const resubmitPayload = {
                contract_id: payload.contract_id,
                preferred_cpt_code_id: payload.preferred_cpt_code_id,
                service_location_id: payload.service_location_id,
                diagnoses: payload.diagnoses,
                expected_amount: payload.expected_amount,
            };

            axios
                .post(
                    `/api/organizations/${
                        this.organization.id
                    }/secondary_invoices/${claimId}/create`,
                    resubmitPayload
                )
                .then(r => {
                    this.$emit("fetch-page");
                    this.resetModal();
                })
                .catch(e => {
                    console.error(e);
                    const errors = _.get(e, "response.data.errors", {});
                    const message = _.get(e, "response.data.message", {});
                    if (
                        typeof errors === "object" &&
                        Object.keys(errors) &&
                        Object.keys(errors).length
                    ) {
                        this.setErrors({ ...errors });
                    } else if (typeof message === "string") {
                        this.setErrors([{ message }]);
                    } else if (isArray(message)) {
                        this.setErrors([...message]);
                    }
                });
        },

        validatePayload() {
            if (!this.payload.therapist_id) {
                this.setErrors({ Error: [`Therapist must be selected`] });
                return false;
            }
            if (!this.payload.therapist_id) {
                this.setErrors({ Error: [`Therapist must be selected`] });
                return false;
            }
            if (_.get(this.contract, "funding_source.type", null) === "insurance_provider") {
                if (!_.get(this.payload, "service_location_id", null)) {
                    this.setErrors({ Error: [`Place of service must be selected.`] });
                    return false;
                }

                if (!_.get(this.payload, "preferred_cpt_code_id", null)) {
                    this.setErrors({ Error: [`CPT code must be selected.`] });
                    return false;
                }
                if (!_.get(this.payload, "expected_amount", null)) {
                    this.setErrors({ Error: [`Expected amount must be greater than zero.`] });
                    return false;
                }
                if (!_.get(this.payload, "service_hours", null)) {
                    this.setErrors({ Error: [`Service hours must be greater than zero.`] });
                    return false;
                }
                if (!_.get(this.payload, "diagnoses", []).length) {
                    this.setErrors({ Error: [`At least one diagnosis code must be selected.`] });
                    return false;
                }
                if (
                    !this.payload.serviced_at ||
                    this.moment(this.payload.serviced_at).isSameOrAfter(
                        this.moment()
                            .hour(23)
                            .minute(59)
                            .second(59)
                    )
                ) {
                    this.setErrors({ Error: [`Service date can not be empty or in the future.`] });
                    return false;
                }
            }
            return true;
        },

        resetModal() {
            this.success = false;
            this.newContract = false;
            this.visible = false;
            this.payload = this.generatePayload();
        },

        prettyType(type) {
            let spaced = type.replace(/_/g, " ");
            spaced = spaced.split(" ");
            for (let i = 0; i < spaced.length; i++) {
                spaced[i] = spaced[i][0].toUpperCase() + spaced[i].substr(1);
            }
            return spaced.join(" ");
        },
    },
};
</script>

<style scoped>
.hide {
    max-width: 0;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}
.border-button {
    border-top-right-radius: 0.2rem !important;
    border-bottom-right-radius: 0.2rem !important;
}

.enlarge {
    transform-origin: center;
    transform: scale(1.5);
}
</style>
